// Gamepad / wheel calibration (RALLY_GOAL.md §5 P1-5): watch every connected controller and work
// out which button or axis the player used. Pure over a list of gamepad snapshots, so it is tested
// without a browser.
import type { PadBinding } from './devices';

export type CaptureKind = 'button' | 'pedal' | 'steer';

export type CaptureResult =
  | { kind: 'button' | 'pedal'; binding: PadBinding }
  | { kind: 'steer'; axis: number; min: number; center: number; max: number };

export interface PadSnapshot {
  index: number;
  axes: readonly number[];
  buttons: readonly { pressed: boolean; value: number }[];
}

/** Seconds to keep following a pedal to its end stop, and to wait for lock-to-lock steering. */
const PEDAL_FOLLOW = 0.8;
const STEER_FOLLOW = 3;
const TIMEOUT = 10;

export class PadCapture {
  private base: Map<number, { axes: number[]; buttons: boolean[] }> | null = null;
  private axis: { pad: number; axis: number; rest: number; min: number; max: number; since: number } | null = null;
  private t = 0;
  result: CaptureResult | null = null;
  timedOut = false;

  constructor(readonly kind: CaptureKind) {}

  get done(): boolean {
    return this.result !== null || this.timedOut;
  }

  /** Seconds left before the capture gives up. */
  get remaining(): number {
    return Math.max(0, TIMEOUT - this.t);
  }

  /** Per frame with the time step and the current controllers; returns true when finished. */
  poll(dt: number, pads: readonly (PadSnapshot | null)[]): boolean {
    if (this.done) return true;
    this.t += dt;
    if (this.t > TIMEOUT) {
      this.timedOut = true;
      return true;
    }
    if (!this.base) {
      // Baseline: where every axis rests and which buttons are already held.
      this.base = new Map();
      for (const p of pads) if (p) this.base.set(p.index, { axes: [...p.axes], buttons: p.buttons.map((b) => b.pressed) });
      return false;
    }
    for (const p of pads) {
      if (!p) continue;
      const base = this.base.get(p.index);
      if (!base) {
        this.base.set(p.index, { axes: [...p.axes], buttons: p.buttons.map((b) => b.pressed) });
        continue;
      }
      if (this.kind !== 'steer' && !this.axis) {
        for (let i = 0; i < p.buttons.length; i++) {
          if (p.buttons[i].pressed && !base.buttons[i] && (this.kind === 'button' || p.buttons[i].value > 0.5)) {
            this.result = { kind: this.kind, binding: { button: i } };
            return true;
          }
        }
      }
      if (this.kind === 'button') continue;
      if (!this.axis) {
        // The axis that has moved furthest from rest, past a threshold.
        let best = -1;
        let dev = this.kind === 'steer' ? 0.3 : 0.4;
        for (let i = 0; i < p.axes.length; i++) {
          const d = Math.abs(p.axes[i] - (base.axes[i] ?? 0));
          if (d > dev) {
            dev = d;
            best = i;
          }
        }
        if (best >= 0) {
          const rest = base.axes[best] ?? 0;
          this.axis = { pad: p.index, axis: best, rest, min: Math.min(rest, p.axes[best]), max: Math.max(rest, p.axes[best]), since: this.t };
        }
      } else if (this.axis.pad === p.index) {
        const a = this.axis;
        const v = p.axes[a.axis] ?? a.rest;
        a.min = Math.min(a.min, v);
        a.max = Math.max(a.max, v);
        const follow = this.kind === 'steer' ? STEER_FOLLOW : PEDAL_FOLLOW;
        if (this.t - a.since >= follow) {
          if (this.kind === 'pedal') {
            const full = Math.abs(a.max - a.rest) > Math.abs(a.min - a.rest) ? a.max : a.min;
            this.result = { kind: 'pedal', binding: { axis: a.axis, dir: full >= a.rest ? 1 : -1, rest: a.rest, full } };
          } else if (a.max - a.min > 0.6) {
            this.result = { kind: 'steer', axis: a.axis, min: a.min, center: a.rest, max: a.max };
          } else {
            // Not turned far enough: keep watching.
            a.since = this.t;
            return false;
          }
          return true;
        }
      }
    }
    return false;
  }
}

/** Short label for a binding ("AXIS 2 ⟷", "BUTTON 7"). */
export function bindingLabel(b: PadBinding): string {
  if ('button' in b) return `BUTTON ${b.button}`;
  return b.rest !== undefined ? `AXIS ${b.axis} (CALIBRATED)` : `AXIS ${b.axis}${b.dir > 0 ? '+' : '−'}`;
}

/** Display name for a KeyboardEvent.code. */
export function keyLabel(code: string): string {
  const named: Record<string, string> = { ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', Space: 'SPACE', Escape: 'ESC', Enter: 'ENTER', Backspace: 'BACKSPACE', Tab: 'TAB', ShiftLeft: 'L SHIFT', ShiftRight: 'R SHIFT', ControlLeft: 'L CTRL', ControlRight: 'R CTRL', AltLeft: 'L ALT', AltRight: 'R ALT' };
  if (named[code]) return named[code];
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad/.test(code)) return `NUM ${code.slice(6).toUpperCase()}`;
  return code.toUpperCase();
}
